import Hammer from 'hammerjs';
import { MutableRefObject } from 'react';
import { vars } from 'src/core/themes';
import { Colors } from 'src/core/themes/constants/colors';
import { SECTION_TYPES_WITH_ROWS } from 'src/utils/venueConfigUtils';
import { SectionInfo } from 'src/WebApiController';

export const isMissiongSectionData = ({ rows, sectionType }: SectionInfo) => {
  // Only check on sections with rows, others are considered valid always
  if (!SECTION_TYPES_WITH_ROWS.includes(sectionType)) {
    return false;
  }

  // If all rows are speculative, we consider it missing data
  if (rows.every((r) => r.isSpeculative)) {
    return true;
  }

  // If any row is not speculative and has no ordinal, we consider it missing data
  if (
    rows.some(({ isSpeculative, ordinal }) => !isSpeculative && ordinal == null)
  ) {
    return true;
  }

  return false;
};

export type DrawingPoint = { x: number; y: number };

const checkIntersection = (
  rect: DOMRect,
  pointA: DrawingPoint,
  pointB: DrawingPoint
) => {
  // Get the corners of the bounding box
  const topLeft = { x: rect.left, y: rect.top };
  const topRight = { x: rect.right, y: rect.top };
  const bottomLeft = { x: rect.left, y: rect.bottom };
  const bottomRight = { x: rect.right, y: rect.bottom };

  // Check if the line intersects any of the four sides of the box
  return (
    lineIntersects(pointA, pointB, topLeft, topRight) || // Top side
    lineIntersects(pointA, pointB, topRight, bottomRight) || // Right side
    lineIntersects(pointA, pointB, bottomRight, bottomLeft) || // Bottom side
    lineIntersects(pointA, pointB, bottomLeft, topLeft) // Left side
  );
};

// Helper function to check if two line segments intersect
const lineIntersects = (
  pointA: DrawingPoint,
  pointB: DrawingPoint,
  compareA: DrawingPoint,
  compareB: DrawingPoint
) => {
  // Calculate the direction of the lines
  const denominator =
    (pointA.x - pointB.x) * (compareA.y - compareB.y) -
    (pointA.y - pointB.y) * (compareA.x - compareB.x);
  if (denominator === 0) return false; // Lines are parallel

  const t =
    ((pointA.x - compareA.x) * (compareA.y - compareB.y) -
      (pointA.y - compareA.y) * (compareA.x - compareB.x)) /
    denominator;
  const u =
    -(
      (pointA.x - pointB.x) * (pointA.y - compareA.y) -
      (pointA.y - pointB.y) * (pointA.x - compareA.x)
    ) / denominator;

  // Check if the intersection point is within both line segments
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
};

// Check if the DOM rect crosses any of the lines
export const crossesPoints = (points: DrawingPoint[], rect: DOMRect) => {
  if (points.length < 2) {
    return false;
  }
  for (let index = 1; index < points.length; index++) {
    if (checkIntersection(rect, points[index - 1], points[index])) {
      return true;
    }
  }
  return false;
};

// Draw the lines in the canvas
const drawLines = (
  context: CanvasRenderingContext2D,
  points: DrawingPoint[],
  isDarkMode: boolean
) => {
  if (points.length < 2) {
    return;
  }
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.strokeStyle = isDarkMode
    ? Colors.backgroundStubhubSeatmap
    : Colors.indigo100;
  context.beginPath();
  context.moveTo(points[0].x, points[0].y);
  for (let index = 1; index < points.length; index++) {
    context.lineTo(points[index].x, points[index].y);
  }
  context.stroke();
  context.closePath();
};

export const registerDrawLine = (
  svgElementRef: MutableRefObject<HTMLDivElement | undefined | null>,
  onDoubleClickStart: MutableRefObject<() => void>,
  onDoubleClickDone: MutableRefObject<(points: DrawingPoint[]) => void>,
  allowDrawLine = true,
  isDarkMode = false
) => {
  if (
    svgElementRef.current == null ||
    svgElementRef.current.parentElement === null ||
    !allowDrawLine
  ) {
    return () => {};
  }

  let element: HTMLCanvasElement | null = null;
  let context: CanvasRenderingContext2D | null = null;
  // Points in the canvas to draw
  const points: DrawingPoint[] = [];
  // Points in the screen to report
  const rectPoints: DrawingPoint[] = [];

  const resetCanvas = () => {
    if (
      svgElementRef.current == null ||
      svgElementRef.current.parentElement === null
    ) {
      return;
    }
    const curreParent = svgElementRef.current.parentElement as HTMLDivElement;
    if (curreParent != null && element !== null) {
      curreParent.removeChild(element);
      curreParent.style.cursor = 'default';
      element = null;
      points.length = 0;
      rectPoints.length = 0;
    }
    if (context !== null) {
      context.clearRect(0, 0, context.canvas.width, context.canvas.height);
      context = null;
    }
  };

  const onDoubleClick = (event: MouseEvent) => {
    if (
      svgElementRef.current == null ||
      svgElementRef.current.parentElement === null
    ) {
      return;
    }
    if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    // Double click not started, start it
    if (element == null && onDoubleClickStart.current) {
      const curreParent = svgElementRef.current.parentElement as HTMLDivElement;
      const { left: offsetX, top: offsetY } =
        svgElementRef.current.getBoundingClientRect();
      onDoubleClickStart.current();
      points.push({ x: event.clientX - offsetX, y: event.clientY - offsetY });
      rectPoints.push({ x: event.clientX, y: event.clientY });
      element = document.createElement('canvas');
      element.width = curreParent.clientWidth;
      element.height = curreParent.clientHeight;
      element.style.position = 'absolute';
      element.style.left = `0px`;
      element.style.top = `0px`;
      context = element.getContext('2d');
      curreParent.appendChild(element);
      curreParent.style.cursor = 'crosshair';
      return;
    }

    onDoubleClickDone.current && onDoubleClickDone.current(rectPoints);
    resetCanvas();
  };
  const onClick = (event: MouseEvent) => {
    if (
      svgElementRef.current == null ||
      svgElementRef.current.parentElement === null ||
      element === null ||
      context === null
    ) {
      return;
    }
    const { left: offsetX, top: offsetY } =
      svgElementRef.current.getBoundingClientRect();
    points.push({ x: event.clientX - offsetX, y: event.clientY - offsetY });
    rectPoints.push({ x: event.clientX, y: event.clientY });
    if (points.length > 1) {
      drawLines(context, points, isDarkMode);
    }
  };

  const onMouseMove = (event: MouseEvent) => {
    if (
      svgElementRef.current == null ||
      svgElementRef.current.parentElement === null ||
      element === null ||
      context === null
    ) {
      return;
    }

    const { left: offsetX, top: offsetY } =
      svgElementRef.current.getBoundingClientRect();
    const currentPoint = {
      x: event.clientX - offsetX,
      y: event.clientY - offsetY,
    };
    drawLines(context, [...points, currentPoint], isDarkMode);
  };
  const onMouseLeave = (_: MouseEvent) => {
    if (element !== null && context !== null) {
      onDoubleClickDone.current && onDoubleClickDone.current(rectPoints);
    }
    resetCanvas();
  };

  const parentElement = svgElementRef.current.parentElement as HTMLDivElement;
  // Trick to make the parent focusable so that it can receive keyboard events
  parentElement.setAttribute('tabindex', '0');
  // Remove the outline when the parent is focused
  parentElement.addEventListener('focus', () => {
    parentElement.style.outline = 'none';
  });

  parentElement.addEventListener('dblclick', onDoubleClick);
  parentElement.addEventListener('click', onClick);
  parentElement.addEventListener('mousemove', onMouseMove);
  parentElement.addEventListener('mouseleave', onMouseLeave);

  // Escape key to cancel the drawing
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onDoubleClickDone.current && onDoubleClickDone.current(rectPoints);
      resetCanvas();
    }
  };
  parentElement.addEventListener('keydown', onKeyDown);

  return () => {
    parentElement?.removeEventListener('dblclick', onDoubleClick);
    parentElement?.removeEventListener('click', onClick);
    parentElement?.removeEventListener('mousemove', onMouseMove);
    parentElement?.removeEventListener('mouseleave', onMouseLeave);
    parentElement?.removeEventListener('keydown', onKeyDown);

    resetCanvas();
  };
};

// Relax the panning detection, panning when moved at least 1 pixel
const moved = (a: MouseEvent | null, b: MouseEvent | null): boolean => {
  if (a === null || b === null) {
    return false;
  }
  return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2)) > 1;
};

export const registerPanningListners = (
  svgElement: HTMLElement | undefined | null,
  isPanning: React.MutableRefObject<boolean>
) => {
  // This is a hack to check if user is panning or clicking
  let willClick = false;
  let downEvent: MouseEvent | null = null;

  const panningHandlers = {
    mousedown: (event: Event) => {
      willClick = true;
      downEvent = event as MouseEvent;
    },
    mousemove: (event: Event) => {
      if (
        willClick &&
        !isPanning.current &&
        moved(downEvent as MouseEvent, event as MouseEvent)
      ) {
        isPanning.current = true;
      }
    },
    mouseup: (_: Event) => {
      downEvent = null;
      if (willClick) {
        // Click event is always fired after mouseup,
        // reserve a small time on the panning flag to avoid click and pan at the same time
        setTimeout(() => {
          isPanning.current = false;
          willClick = false;
        }, 50);
      }
    },
  } as Record<string, (event: Event) => void>;

  for (const eventName in panningHandlers) {
    svgElement?.addEventListener(eventName, panningHandlers[eventName]);
  }

  return () => {
    for (const eventName in panningHandlers) {
      svgElement?.removeEventListener(eventName, panningHandlers[eventName]);
    }
  };
};

/**
 * Registers tap-and-hold gesture detection on a list of SVG elements
 *
 * The tap-and-hold gesture is triggered when:
 * 1. User presses down (touchstart/mousedown)
 * 2. Holds for at least 500ms without moving more than 10px
 * 3. Releases (touchend/mouseup)
 *
 * This follows mobile platform conventions where the action occurs on release
 * rather than during the hold, giving users control to cancel by moving away.
 *
 * @param svgElements - Array of SVG elements to attach gesture listeners to
 * @param onTapAndHold - Callback function called when tap-and-hold gesture completes
 * @returns Cleanup function that removes all event listeners from all elements
 */
export const registerTapAndHold = (
  svgElements: (SVGElement | undefined | null)[],
  onTapAndHold: (e: Event) => void,
  enabled: boolean
): (() => void) => {
  if (!enabled) {
    return () => undefined;
  }
  // Filter out null/undefined elements
  const validElements = svgElements.filter(
    (element): element is SVGElement => element != null
  );

  if (validElements.length === 0) {
    return () => {};
  }

  // Track gesture state
  let startTime: number | null = null;
  let startPosition: { x: number; y: number } | null = null;
  let startEvent: TouchEvent | MouseEvent | null = null;
  let tapAndHoldTriggered = false; // Flag to prevent click event after tap-and-hold

  // Configuration constants
  const holdTime = 500; // milliseconds threshold for tap-and-hold
  const threshold = 10; // maximum movement allowed in pixels before cancelling

  /**
   * Resets all gesture tracking state
   */
  const clearState = () => {
    startTime = null;
    startPosition = null;
    startEvent = null;
    // Don't reset tapAndHoldTriggered here - let it persist until next touch
  };

  /**
   * Extracts position coordinates from touch or mouse events
   * @param event - TouchEvent or MouseEvent
   * @returns Object with x, y coordinates in viewport pixels
   */
  const getEventPosition = (event: TouchEvent | MouseEvent) => {
    if ('touches' in event && event.touches.length > 0) {
      // Touch event - use first touch point
      return {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
    } else if ('clientX' in event) {
      // Mouse event
      return {
        x: event.clientX,
        y: event.clientY,
      };
    }
    return { x: 0, y: 0 };
  };

  /**
   * Calculates Euclidean distance between two points
   * @param pos1 - First position with x, y coordinates
   * @param pos2 - Second position with x, y coordinates
   * @returns Distance in pixels
   */
  const getDistance = (
    pos1: { x: number; y: number },
    pos2: { x: number; y: number }
  ) => {
    return Math.sqrt(
      Math.pow(pos2.x - pos1.x, 2) + Math.pow(pos2.y - pos1.y, 2)
    );
  };

  /**
   * Handles the start of a potential tap-and-hold gesture
   * Records the start time, position, and event for later validation
   */
  const handleStart = (event: TouchEvent | MouseEvent) => {
    clearState();
    tapAndHoldTriggered = false; // Reset flag for new touch sequence

    const position = getEventPosition(event);
    startPosition = position;
    startTime = Date.now();
    startEvent = event;
  };

  /**
   * Handles movement during a potential tap-and-hold gesture
   * Cancels the gesture if movement exceeds the allowed threshold (10px)
   */
  const handleMove = (event: TouchEvent | MouseEvent) => {
    if (startPosition && startTime) {
      const currentPosition = getEventPosition(event);
      const distance = getDistance(startPosition, currentPosition);

      // If movement exceeds threshold, cancel tap-and-hold
      if (distance > threshold) {
        clearState();
      }
    }
  };

  /**
   * Handles the end of a potential tap-and-hold gesture
   * Triggers the callback only if the hold duration was ≥ 500ms
   * This follows mobile platform conventions where action occurs on release
   */
  const handleEnd = (event: TouchEvent | MouseEvent) => {
    if (startTime && startPosition && startEvent) {
      const holdDuration = Date.now() - startTime;

      // Only trigger tap-and-hold if held longer than threshold
      if (holdDuration >= holdTime) {
        tapAndHoldTriggered = true;
        onTapAndHold(startEvent);

        // Prevent the subsequent click event from firing
        if ('preventDefault' in event) {
          event.preventDefault();
        }
        if ('stopPropagation' in event) {
          event.stopPropagation();
        }
      }
    }

    clearState();
  };

  /**
   * Intercepts click events to prevent them when tap-and-hold was triggered
   * This prevents double-action when user performs a tap-and-hold gesture
   */
  const handleClick = (event: Event) => {
    if (tapAndHoldTriggered) {
      event.preventDefault();
      event.stopPropagation();
      tapAndHoldTriggered = false; // Reset for next interaction
      return false;
    }
  };

  // Register touch event listeners for tap-and-hold detection on all valid elements
  // Note: passive: false allows preventDefault() if needed for gesture control
  validElements.forEach((element) => {
    element.addEventListener('touchstart', handleStart as EventListener, {
      passive: false,
    });
    element.addEventListener('touchmove', handleMove as EventListener, {
      passive: false,
    });
    element.addEventListener('touchend', handleEnd as EventListener, {
      passive: false,
    });

    // Add click handler to prevent conflicts with tap-and-hold
    // This must be added with capture: true to intercept before other click handlers
    element.addEventListener('click', handleClick, {
      capture: true,
    });
  });

  /**
   * Cleanup function that removes all event listeners from all elements and clears state
   * Call this when the component unmounts or the elements change
   * @returns void
   */
  return () => {
    clearState();
    tapAndHoldTriggered = false; // Reset flag on cleanup
    validElements.forEach((element) => {
      element.removeEventListener('touchstart', handleStart as EventListener);
      element.removeEventListener('touchmove', handleMove as EventListener);
      element.removeEventListener('touchend', handleEnd as EventListener);
      element.removeEventListener('click', handleClick, {
        capture: true,
      });
    });
  };
};

export const addStrokeGradient = (root: SVGElement, isDarkMode: boolean) => {
  const existingGradient = root.querySelector('#strokeGradient');
  if (existingGradient) {
    return;
  }
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const linearGradient = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'linearGradient'
  );
  linearGradient.setAttribute('id', 'strokeGradient');
  linearGradient.setAttribute('x1', '0%');
  linearGradient.setAttribute('y1', '0%');
  linearGradient.setAttribute('x2', '100%');
  linearGradient.setAttribute('y2', '0%');

  const offsetStart = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'stop'
  );
  offsetStart.setAttribute('offset', '0%');
  offsetStart.setAttribute('stop-color', vars.color.backgroundBrandInverted);
  const startAnimation = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'animate'
  );
  startAnimation.setAttribute('attributeName', 'offset');
  startAnimation.setAttribute('values', '0;1;0');
  startAnimation.setAttribute('dur', '3s');
  startAnimation.setAttribute('repeatCount', 'indefinite');
  offsetStart.appendChild(startAnimation);

  const offsetHigh = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'stop'
  );
  offsetHigh.setAttribute('offset', '100%');
  offsetHigh.setAttribute('stop-color', vars.color.backgroundBrand);
  const endAnimation = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'animate'
  );
  endAnimation.setAttribute('attributeName', 'offset');
  endAnimation.setAttribute('values', '1;2;1');
  endAnimation.setAttribute('dur', '3s');
  endAnimation.setAttribute('repeatCount', 'indefinite');
  offsetHigh.appendChild(endAnimation);

  linearGradient.appendChild(offsetStart);
  linearGradient.appendChild(offsetHigh);
  defs.appendChild(linearGradient);
  root.appendChild(defs);
};

export const addSectionAnimation = (sectionPath: SVGElement) => {
  sectionPath.setAttribute('stroke', 'url(#strokeGradient');
  sectionPath.setAttribute('fill', vars.color.backgroundPrimary);
  sectionPath.setAttribute('pathLength', '100');
};

export const tryCleanAnimation = (sectionPath: SVGElement) => {
  sectionPath.removeAttribute('stroke');
  sectionPath.removeAttribute('pathLength');
};

// Mobile touch support for the SvgPanZoom
export const svgPanZoomMobileHandler = {
  // Halt all touch events
  haltEventListeners: [
    'touchstart',
    'touchend',
    'touchmove',
    'touchleave',
    'touchcancel',
  ],

  // Init custom events handler
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  init: function (this: any, options: any) {
    const instance = options.instance;
    let initialScale = 0.75;
    let pannedX = 0;
    let pannedY = 0;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - this exist in Hammer - but the TypeDef of Hammer is older than the installed version (there's no new one available)
    const inputClass = Hammer.SUPPORT_POINTER_EVENTS
      ? Hammer.PointerEventInput
      : Hammer.TouchInput;

    this.hammer = new Hammer(options.svgElement, {
      inputClass: inputClass,
    });

    this.hammer.get('pinch').set({ enable: true });

    this.hammer.on('doubletap', function () {
      instance.zoomIn();
    });

    this.hammer.on(
      'panstart panmove',
      function (ev: {
        type: string;
        deltaX: number;
        deltaY: number;
        srcEvent?: MouseEvent;
      }) {
        // On pan start reset panned variables
        if (ev.type === 'panstart') {
          pannedX = 0;
          pannedY = 0;
        }

        // Pan only the difference
        instance.panBy({
          x: ev.deltaX - pannedX,
          y: ev.deltaY - pannedY,
        });
        pannedX = ev.deltaX;
        pannedY = ev.deltaY;
        ev.srcEvent?.stopPropagation();
      }
    );

    this.hammer.on(
      'pinchstart pinchmove',
      function (ev: { type: string; scale: number; srcEvent?: MouseEvent }) {
        // On pinch start remember initial zoom
        if (ev.type === 'pinchstart') {
          initialScale = instance.getZoom();
          instance.zoom(initialScale * ev.scale);
        }

        instance.zoom(initialScale * ev.scale);
        ev.srcEvent?.stopPropagation();
      }
    );
  },

  // Destroy custom events handler
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  destroy: function (this: any) {
    this.hammer.destroy();
  },
};

/**
 * Validates that an SVG element has valid dimensions
 * to prevent non-finite SVGMatrix errors
 */
export const isSvgValid = (
  element: SVGSVGElement | null | undefined
): boolean => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    isFinite(rect.width) &&
    isFinite(rect.height)
  );
};
